/**
 * The snapshot, turned into the numbers the screens actually show.
 *
 * All of the arithmetic comes from `@alohamula/domain` — the same module the
 * Lambdas import — so a balance shown here is the balance the backend would
 * compute. Nothing in this file does money math itself.
 */

import { useMemo } from 'react';
import {
  buildIndex,
  budgetItemBalances,
  deriveAlerts,
  leftToSpend,
  ledgerTotals,
  moneySpent,
  monthlyAmountOf,
  paycheckRemainder,
  pendingSummary,
  periodContains,
  perPaycheckFromMonthly,
  setPayDays,
  sumCents,
  suggestCategories,
  uncategorizedTransactions,
  type BudgetAlert,
  type BudgetIndex,
  type BudgetItem,
  type BudgetItemBalance,
  type PaycheckPeriod,
  type Snapshot,
  type Transaction,
} from '@alohamula/domain';

export interface Budget {
  snapshot: Snapshot;
  index: BudgetIndex;
  period: PaycheckPeriod;

  envelopes: BudgetItemBalance[];
  bills: BudgetItemBalance[];
  activeItems: BudgetItem[];
  itemsById: Map<string, BudgetItem>;

  /** Non-trashed transactions, newest first. */
  transactions: Transaction[];
  transactionsInPeriod: Transaction[];
  uncategorized: Transaction[];
  suggestions: Map<string, { budgetItemId: string; source: 'rule' | 'builtIn'; matchedKeyword: string }>;

  spentThisPeriod: number;
  leftThisPeriod: number;
  envelopesFunded: number;
  billsFunded: number;
  unallocatedPaycheck: number;

  pending: ReturnType<typeof pendingSummary>;
  totals: ReturnType<typeof ledgerTotals>;
  alerts: BudgetAlert[];
}

export function useBudget(snapshot: Snapshot, period: PaycheckPeriod, now: Date): Budget {
  return useMemo(() => {
    const transactions = snapshot.transactions
      .filter((transaction) => transaction.deletedAt == null)
      .sort(compareByDateDescending);

    const index = buildIndex(transactions, snapshot.allocations);
    // Alphabetical everywhere an item is listed or picked. The snapshot's own
    // order is sortOrder, which nothing in either app sets.
    const activeItems = snapshot.budgetItems
      .filter((item) => item.isActive)
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));
    const balances = budgetItemBalances(
      activeItems,
      index,
      period,
      snapshot.paycheckSettings.rolloverKickoffDate
    );

    const uncategorized = uncategorizedTransactions(transactions, index)
      .filter((transaction) => transaction.amount < 0)
      .sort(compareByDateDescending);

    const { paycheckAmount, payDays } = snapshot.paycheckSettings;
    setPayDays(payDays);
    const envelopeBalances = balances.filter((balance) => balance.item.kind === 'envelope');
    const billBalances = balances.filter((balance) => balance.item.kind === 'bill');
    const envelopesFunded = perPaycheckFromMonthly(
      sumCents(
        envelopeBalances
          .filter((balance) => !balance.item.parentId)
          .map((balance) => monthlyAmountOf(balance.item))
      )
    );
    const billsFunded = perPaycheckFromMonthly(
      sumCents(
        billBalances
          .filter((balance) => !balance.item.parentId)
          .map((balance) => monthlyAmountOf(balance.item))
      )
    );

    return {
      snapshot,
      index,
      period,

      envelopes: envelopeBalances,
      bills: billBalances,
      activeItems,
      itemsById: new Map(snapshot.budgetItems.map((item) => [item.id, item])),

      transactions,
      transactionsInPeriod: transactions.filter((transaction) =>
        periodContains(period, transaction.transactionDate)
      ),
      uncategorized,
      suggestions: suggestCategories(uncategorized, snapshot.sortingRules, activeItems),

      spentThisPeriod: moneySpent(transactions, period),
      leftThisPeriod: leftToSpend(transactions, period, paycheckAmount),
      envelopesFunded,
      billsFunded,
      unallocatedPaycheck: paycheckRemainder(paycheckAmount, billsFunded, envelopesFunded),

      pending: pendingSummary(transactions, now),
      totals: ledgerTotals(transactions),
      alerts: deriveAlerts({
        budgetItems: snapshot.budgetItems,
        transactions,
        index,
        period,
        now,
        rolloverKickoffDate: snapshot.paycheckSettings.rolloverKickoffDate,
      }),
    };
  }, [snapshot, period, now]);
}

/**
 * Newest first, with the transaction id breaking ties. Ledger dates have no
 * time, so two charges on the same day would otherwise reorder between renders.
 */
function compareByDateDescending(a: Transaction, b: Transaction): number {
  const byDate = b.transactionDate.localeCompare(a.transactionDate);
  if (byDate !== 0) return byDate;
  return b.id.localeCompare(a.id);
}
